import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { contactsAPI } from "../lib/api";
import { formatDate, getDirectionLabel, getDirectionColor } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  UserCircle,
  FileText,
  Mail,
  Phone,
  Users,
  Building2,
  Briefcase,
  Building,
} from "lucide-react";

const CATEGORIES = {
  clienti: { label: "Clienti", icon: Users, color: "bg-blue-100 text-blue-700" },
  fornitori: { label: "Fornitori", icon: Building2, color: "bg-emerald-100 text-emerald-700" },
  dipendenti: { label: "Dipendenti", icon: Briefcase, color: "bg-amber-100 text-amber-700" },
  altri_enti: { label: "Altri Enti", icon: Building, color: "bg-purple-100 text-purple-700" },
};

export function ContactProtocolsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [contactRes, protocolsRes] = await Promise.all([
        contactsAPI.getOne(id),
        contactsAPI.getProtocols(id),
      ]);
      setContact(contactRes.data);
      setProtocols(protocolsRes.data);
    } catch (error) {
      console.error("Error loading contact:", error);
      navigate("/settings/contacts");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (!contact) return null;

  const catInfo = CATEGORIES[contact.category] || CATEGORIES.altri_enti;
  const CatIcon = catInfo.icon;

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/settings/contacts")}
          className="text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={24} />
        </Button>
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-lg ${catInfo.color}`}>
            <CatIcon size={28} />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">
              {contact.name}
            </h1>
            <div className="flex items-center gap-3 text-muted-foreground">
              <Badge variant="outline" className={catInfo.color}>
                {catInfo.label}
              </Badge>
              {contact.email && (
                <span className="flex items-center gap-1">
                  <Mail size={14} /> {contact.email}
                </span>
              )}
              {contact.phone && (
                <span className="flex items-center gap-1">
                  <Phone size={14} /> {contact.phone}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      {contact.notes && (
        <Card className="bg-muted/50 border-border">
          <CardContent className="p-4">
            <p className="text-foreground">{contact.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Protocols */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
            <FileText size={20} className="text-[#2D7BB7]" />
            Protocolli ({protocols.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {protocols.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText size={48} className="mx-auto mb-4 opacity-50" />
              <p>Nessun protocollo associato a questo contatto</p>
            </div>
          ) : (
            <div className="space-y-3">
              {protocols.map((protocol) => (
                <Link
                  key={protocol.id}
                  to={`/protocols/${protocol.id}`}
                  className="block p-4 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="font-mono font-medium text-[#2D7BB7]">
                        {protocol.protocol_number}
                      </span>
                      <Badge className={`${getDirectionColor(protocol.direction)} border`}>
                        {getDirectionLabel(protocol.direction)}
                      </Badge>
                      <span className="text-foreground">{protocol.subject}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {formatDate(protocol.created_at)}
                    </span>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">
                    {protocol.direction === "entrata" ? "Da:" : "A:"}{" "}
                    {protocol.direction === "entrata" ? protocol.sender : protocol.recipient}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
